import React, { useId } from 'react';
import Svg, { ClipPath, Defs, G, LinearGradient, Path, Stop } from 'react-native-svg';

export const DEVICE_USAGE_SPOT_SIZES = ['sm', 'md', 'lg'] as const;
type DeviceUsageSpotSize = (typeof DEVICE_USAGE_SPOT_SIZES)[number];

const SVG_SIZES = {
    sm: 80,
    md: 138,
    lg: 160,
};

const getSvgSize = (size: DeviceUsageSpotSize): number => SVG_SIZES[size];

interface DeviceUsageSvgProps {
    /**
     * Size of the spot illustration `sm`, `md`, `lg`.
     */
    size?: DeviceUsageSpotSize | number;
    /**
     * Provides a human-readable title for the element that contains it.
     * https://www.w3.org/TR/SVG-access/#Equivalent
     */
    title?: string;
}

export function DeviceUsageSvg({ title, size = 'md' }: DeviceUsageSvgProps) {
    const componentId = useId();
    const clipPathId = `spot-device-usage-1__svgClip-${componentId}`;
    const paint0Id = `spot-device-usage-1__paint0_linear_4373_1766-${componentId}`;
    const paint1Id = `spot-device-usage-1__paint1_linear_4373_1766-${componentId}`;
    const paint2Id = `spot-device-usage-1__paint2_linear_4373_1766-${componentId}`;
    const paint3Id = `spot-device-usage-1__paint3_linear_4373_1766-${componentId}`;
    const paint4Id = `spot-device-usage-1__paint4_linear_4373_1766-${componentId}`;

    const svgSize = typeof size === 'number' ? size : getSvgSize(size);
    // Maintain aspect ratio (original viewBox is 107x93)
    const svgHeight = (svgSize * 93) / 107;

    return (
        <Svg title={title} viewBox="0 0 107 93" width={svgSize} height={svgHeight} fill="none">
            {/* @ts-expect-error - Defs can have children */}
            <Defs>
                <ClipPath id={clipPathId}>
                    <Path
                        fill="#fff"
                        fillRule="evenodd"
                        d="M0 92.718h106.744l-19.36-73.813-17.637 5.183 1.19-9.523c.284-2.274-1.534-4.418-3.131-4.654l-2.398-.354.916-6.675L46.388.21l-.807 6.424-1.193-.176c-1.614-.238-4.34 1.27-4.608 3.568l-2.49 21.413-.553-1.069c-.74-1.43-2.247-2.33-3.85-2.217-.364.026-.66.095-.795.23-.334.334-.188 3.777-.068 6.575.014.338-.05.898-.14 1.493l-11.692 3.845L0 92.718z"
                        clipRule="evenodd"
                    />
                </ClipPath>
                <LinearGradient
                    id={paint0Id}
                    x1="40.653"
                    x2="71.46"
                    y1="17.33"
                    y2="104.085"
                    gradientUnits="userSpaceOnUse"
                >
                    <Stop offset=".386" stopColor="#F39C78" />
                    <Stop offset=".681" stopColor="#DF7838" />
                </LinearGradient>
                <LinearGradient
                    id={paint1Id}
                    x1="40.653"
                    x2="71.46"
                    y1="17.33"
                    y2="104.085"
                    gradientUnits="userSpaceOnUse"
                >
                    <Stop offset=".386" stopColor="#F39C78" />
                    <Stop offset=".681" stopColor="#DF7838" />
                </LinearGradient>
                <LinearGradient
                    id={paint2Id}
                    x1="62.71"
                    x2="46.145"
                    y1="89.338"
                    y2="-4.305"
                    gradientUnits="userSpaceOnUse"
                >
                    <Stop offset=".397" stopColor="#1D465C" />
                    <Stop offset=".955" stopColor="#1F5F7B" />
                </LinearGradient>
                <LinearGradient
                    id={paint3Id}
                    x1="67.686"
                    x2="77.169"
                    y1="23.435"
                    y2="46.688"
                    gradientUnits="userSpaceOnUse"
                >
                    <Stop offset=".386" stopColor="#F39C78" />
                    <Stop offset=".681" stopColor="#DF7838" />
                </LinearGradient>
                <LinearGradient
                    id={paint4Id}
                    x1="69.413"
                    x2="64.895"
                    y1="126.257"
                    y2=".613"
                    gradientUnits="userSpaceOnUse"
                >
                    <Stop offset=".484" stopColor="#F39E78" />
                    <Stop offset=".904" stopColor="#FFB286" />
                </LinearGradient>
            </Defs>
            <G clipPath={`url(#${clipPathId})`}>
                <Path
                    fill="#9BB1F0"
                    d="M64.04 19.52l-19.773-2.436L46.388.21l19.936 2.673-2.284 16.639z"
                />
                <Path
                    fill="#F5CB9D"
                    d="M87.384 18.905l-20.173 5.928-3.756 33.617-26.99-4.573 2.331-19.7-18.604 6.118L0 92.718h106.744l-19.36-73.813z"
                />
                <G>
                    <Path
                        fill={`url(#${paint0Id})`}
                        d="M37 49.324l.007-.003-.54 4.554 24.4 4.135v.003L36.46 53.878l.54-4.554z"
                    />
                    <Path
                        fill={`url(#${paint1Id})`}
                        d="M37.29 31.438l-3.013 25.896c-.261 2.243.788 4.237 2.36 4.49l22.129 3.556-7.933 9.192s-.906 1.812-1.086 5.433c-.121 2.44-.654 6.856-1.044 10.085-.124 1.03-.234 1.94-.311 2.626H20.375c1.78-3.861 8.889-19.344 9.364-21.403.542-2.354-1.567-11.765-3.015-14.632-.52-1.029-.78-2.362-.895-3.65-.238-2.576.69-5.123 2.482-6.986.726-.754 2.713-5.758 3.111-7.126 0 0 .644-2.96.602-3.962-.12-2.798-.266-6.24.068-6.575.135-.136.43-.204.795-.23 1.603-.113 3.11.787 3.85 2.217l.552 1.07z"
                    />
                </G>
                <G>
                    <Path
                        fill={`url(#${paint2Id})`}
                        d="M67.806 9.911L44.388 6.457c-1.614-.238-4.34 1.27-4.608 3.568l-5.503 47.31c-.261 2.242.788 4.236 2.36 4.489l24.918 4.004c1.608.26 3.152-1.397 3.44-3.687l5.941-47.575c.285-2.275-1.533-4.42-3.13-4.655zm.308 6.841l-4.662 41.7-26.99-4.574 4.804-40.592c.215-2.074 1.549-3.58 2.966-3.351l21.756 2.686c1.404.23 2.355 2.08 2.126 4.131z"
                    />
                </G>
                <Path
                    fill="#fff"
                    d="M44.267 17.084l19.773 2.437.964-7.021.983.121c1.405.23 2.356 2.08 2.127 4.132l-4.662 41.699-26.991-4.573 4.805-40.593c.214-2.073 1.548-3.58 2.966-3.35l.92.113-.885 7.035z"
                />
                <G>
                    <Path
                        fill={`url(#${paint3Id})`}
                        d="M70.83 41.288l.15-2.495 1.124-1.784a3.046 3.046 0 00.458-1.872l-.21-2.573-.91-3.5-.413-1.896a.91.91 0 00-.87-.716l-.7-.015c-1.417-.03-2.042 1.044-2.049 2.463a6.705 6.705 0 00.212 1.696l.056.213-1.919-.12a2.487 2.487 0 00-2.107 2.413l.37.82a7.43 7.43 0 001.223 1.873l-1.586.933c-.69.919-.854 1.633-.873 2.062l5.266 4.276s.51.36 1.29.954c.266-.26.568-.626.826-.959a3.22 3.22 0 00.662-1.773z"
                    />
                </G>
                <Path
                    fill="#fff"
                    d="M64.337 5.778L47.616 3.697a.205.205 0 01-.178-.23l.189-1.509a.205.205 0 01.229-.178l16.721 2.082a.205.205 0 01.178.23L64.567 5.6a.207.207 0 01-.23.177z"
                />
                <Path
                    fill="#F7C8B8"
                    d="M71.1 27.49s-.728 1.147-1.27 1.63c0 0 .366 1.689.636 1.931.27.243 1.373-.471 1.373-.471l-.74-3.09z"
                />
                <Path
                    fill="#F7C8B8"
                    d="M66.995 31.293s-1.75.845-1.869 1.57c0 0 .664 1.509 1.87 1.87 1.205.363 1.151-1.266 1.151-1.266l-1.152-2.174z"
                />
                <G>
                    <Path
                        fill={`url(#${paint4Id})`}
                        d="M41.864 54.188c.095.189.161.392.198.6l.567 3.263c.066.382.201.749.4 1.08.795 1.33 2.966 5.016 4.02 7.361a8.136 8.136 0 002.319 3.012c3.224 2.594 10.203 7.883 15.066 9.236l.394.32a18.904 18.904 0 015.254 6.74l3.208 6.918H98.3c-3.524-4.519-19.556-25.124-19.395-25.93.18-.905-1.628-10.531-1.628-13.867 0-3.336-9.224-9.855-9.224-9.855l-6.172-5.01a1.885 1.885 0 00-1.192-.423h-.615c-.67 0-1.297-.324-1.687-.868l-3.902-5.47-1.578-3.316a.852.852 0 00-.77-.487h-.789c-.72 0-1.36.461-1.586 1.145a6.787 6.787 0 00-.262 3.207l.215 1.344a10.75 10.75 0 00.92 2.953l7.647 15.98s.957 7.425-2.416 8.422l-5.715-3.724a3.776 3.776 0 01-1.516-1.951l-.362-1.064a4.308 4.308 0 00-.94-1.563l-2.424-2.583a3.094 3.094 0 00-2.256-.978h-.463c-.78 0-1.492.442-1.842 1.139a1.5 1.5 0 000 1.344l1.517 3.025z"
                    />
                </G>
                <Path
                    fill="#F7C8B8"
                    d="M42 54.535s.91-1.511 1.877-2.236l-1.568-2.716s-2.177-.242-2.113 1.327L42 54.535z"
                />
                <Path
                    fill="#F7C8B8"
                    d="M52.908 27.976s-1.085-.396-2.181.358c0 0 .503 2.013.974 2.897 0 0 2.257-.678 2.783.06l-1.576-3.315z"
                />
            </G>
        </Svg>
    );
}

DeviceUsageSvg.displayName = 'DeviceUsageSvg';
